import { useQuery } from '@tanstack/react-query';

import * as Api from 'contracts/api';
import { RequestAgent } from 'modules/request-agent';
import { useNotify } from 'pages/components/SnackBarProvider';
import { ExposedQueryFunctionContext, UseExposedQueryOptions } from 'queries/types';
import { isOkResponse, throwResponseError } from 'queries/utils';

export function usePublicSubscriptionTemplates<TData = Api.V3.SubscriptionTemplates.PublicTemplates.Response>(
    options?: UseExposedQueryOptions<Api.V3.SubscriptionTemplates.PublicTemplates.Response, Error, TData>
) {
    const notify = useNotify();
    return useQuery<Api.V3.SubscriptionTemplates.PublicTemplates.Response, Error, TData>({
        ...options,
        queryKey: Api.V3.SubscriptionTemplates.PublicTemplates.QueryKey(),
        queryFn: (ctx) =>
            fetchPublicSubscriptionTemplates({
                ...ctx,
                onFailure: (error) => notify.openNotification({ level: 'error', message: error.message })
            })
    });
}

export async function fetchPublicSubscriptionTemplates(
    context: ExposedQueryFunctionContext<Api.V3.SubscriptionTemplates.PublicTemplates.Response>
) {
    const agent = context.agent || new RequestAgent();

    try {
        const response = await agent.public.get(Api.V3.SubscriptionTemplates.PublicTemplates.Endpoint());

        if (isOkResponse(response)) {
            const output = response.data.subscriptionTemplates
                .map(({ aliasUrl, ...template }) => ({
                    ...template,
                    aliasUrl: aliasUrl ? (aliasUrl.startsWith('/') ? aliasUrl : `/${aliasUrl}`) : aliasUrl
                }))
                .sort((itemA, itemB) => (itemB.order || 0) - (itemA.order || 0));
            await context?.onSuccess?.(output);
            return output;
        } else {
            throw throwResponseError(response);
        }
    } catch (error) {
        await context?.onFailure?.(error);
        throw error;
    }
}
